import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Form, Button, Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../shared/api';
import PageShell from '../../components/layout/PageShell';
import PageSection from '../../components/layout/PageSection';
import AppButton from '../../components/ui/AppButton';
import { CenteredLoader } from '../../components/LoadingSpinner';
import NumberInput from '../../components/form/NumberInput';

const DEFAULT_AI = {
  id: null,
  use_local_ai: true,
  openai_api_key: '',
  openai_api_key_set: false,
  openai_model: 'gpt-4o-mini',
};

const DEFAULT_EMAIL = {
  email_enabled: false,
  email_host: '',
  email_port: 993,
  email_username: '',
  email_password: '',
  email_password_set: false,
  email_use_ssl: true,
  email_folder: 'INBOX',
  email_allowed_senders: '',
  email_auto_reply: false,
  email_check_interval: 300,
};

const DEFAULT_SMTP = {
  smtp_host: '',
  smtp_port: 587,
  smtp_username: '',
  smtp_password: '',
  smtp_password_set: false,
  smtp_use_tls: true,
  smtp_use_ssl: false,
  smtp_from_email: '',
};

const AssistantSettings = () => {
  const { t } = useTranslation();

  const [saving, setSaving]           = useState(false);
  const [testing, setTesting]         = useState(false);
  const [error, setError]             = useState(null);
  const [success, setSuccess]         = useState(null);
  const [testResult, setTestResult]   = useState(null);

  const [ai, setAi]       = useState(DEFAULT_AI);
  const [em, setEm]       = useState(DEFAULT_EMAIL);
  const [smtp, setSmtp]   = useState(DEFAULT_SMTP);

  // ── fetch ────────────────────────────────────────────────────────────────
  const applySettingsData = (data) => {
    if (!data) return;
    setAi({
      id:                  data.id,
      use_local_ai:        data.use_local_ai ?? true,
      openai_api_key:      '',
      openai_api_key_set:  !!data.openai_api_key_set,
      openai_model:        data.openai_model || 'gpt-4o-mini',
    });
    setEm({
      email_enabled:         data.email_enabled ?? false,
      email_host:            data.email_host || '',
      email_port:            data.email_port ?? 993,
      email_username:        data.email_username || '',
      email_password:        '',
      email_password_set:    !!data.email_password_set,
      email_use_ssl:         data.email_use_ssl ?? true,
      email_folder:          data.email_folder || 'INBOX',
      email_allowed_senders: data.email_allowed_senders || '',
      email_auto_reply:      data.email_auto_reply ?? false,
      email_check_interval:  data.email_check_interval ?? 300,
    });
    setSmtp({
      smtp_host:         data.smtp_host || '',
      smtp_port:         data.smtp_port ?? 587,
      smtp_username:     data.smtp_username || '',
      smtp_password:     '',
      smtp_password_set: !!data.smtp_password_set,
      smtp_use_tls:      data.smtp_use_tls ?? true,
      smtp_use_ssl:      data.smtp_use_ssl ?? false,
      smtp_from_email:   data.smtp_from_email || '',
    });
  };

  const _settingsInitialized = useRef(false);

  const { isLoading: loading, data: settingsData, refetch: refetchSettings } = useQuery({
    queryKey: ['assistant-settings'],
    queryFn: async () => {
      const response = await api.get('settings/api/assistant-settings/');
      return Array.isArray(response) ? response[0] : response;
    },
  });

  // Initialize form state once on first load; allow re-init after explicit refetch (save)
  useEffect(() => {
    if (settingsData && !_settingsInitialized.current) {
      _settingsInitialized.current = true;
      applySettingsData(settingsData);
    }
  }, [settingsData]);

  const fetchSettings = () => {
    _settingsInitialized.current = false; // allow re-apply after explicit refetch (post-save)
    refetchSettings();
  };

  // ── save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      setTestResult(null);

      const payload = {
        use_local_ai:          ai.use_local_ai,
        openai_model:          ai.openai_model,
        email_enabled:         em.email_enabled,
        email_host:            em.email_host,
        email_port:            em.email_port,
        email_username:        em.email_username,
        email_use_ssl:         em.email_use_ssl,
        email_folder:          em.email_folder,
        email_allowed_senders: em.email_allowed_senders,
        email_auto_reply:      em.email_auto_reply,
        email_check_interval:  em.email_check_interval,
        smtp_host:             smtp.smtp_host,
        smtp_port:             smtp.smtp_port,
        smtp_username:         smtp.smtp_username,
        smtp_use_tls:          smtp.smtp_use_tls,
        smtp_use_ssl:          smtp.smtp_use_ssl,
        smtp_from_email:       smtp.smtp_from_email,
      };
      // Only send secrets when the user typed something
      if (ai.openai_api_key.trim())   payload.openai_api_key = ai.openai_api_key.trim();
      if (em.email_password.trim())   payload.email_password = em.email_password.trim();
      if (smtp.smtp_password.trim())  payload.smtp_password  = smtp.smtp_password.trim();

      if (ai.id) {
        await api.patch(`settings/api/assistant-settings/${ai.id}/`, payload);
      } else {
        await api.post('settings/api/assistant-settings/', payload);
      }
      setSuccess(t('settings.assistant.saved'));
      await fetchSettings();
    } catch (err) {
      console.error('Error saving assistant settings:', err);
      setError(t('settings.assistant.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleUnsetApiKey = async () => {
    try {
      setSaving(true);
      setError(null);
      if (ai.id) await api.patch(`settings/api/assistant-settings/${ai.id}/`, { openai_api_key: '' });
      setSuccess(t('settings.assistant.saved'));
      await fetchSettings();
    } catch { setError(t('settings.assistant.errors.saveFailed')); }
    finally { setSaving(false); }
  };

  const handleUnsetEmailPassword = async () => {
    try {
      setSaving(true);
      setError(null);
      if (ai.id) await api.patch(`settings/api/assistant-settings/${ai.id}/`, { email_password: '' });
      setSuccess(t('settings.assistant.saved'));
      await fetchSettings();
    } catch { setError(t('settings.assistant.errors.saveFailed')); }
    finally { setSaving(false); }
  };

  const handleUnsetSmtpPassword = async () => {
    try {
      setSaving(true);
      setError(null);
      if (ai.id) await api.patch(`settings/api/assistant-settings/${ai.id}/`, { smtp_password: '' });
      setSuccess(t('settings.assistant.saved'));
      await fetchSettings();
    } catch { setError(t('settings.assistant.errors.saveFailed')); }
    finally { setSaving(false); }
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      setTestResult(null);
      setError(null);
      const res = await api.post('settings/api/assistant-settings/test_connection/');
      setTestResult({ success: res.success, message: res.message });
    } catch (err) {
      setTestResult({ success: false, message: err?.message || 'Connection failed' });
    } finally {
      setTesting(false);
    }
  };

  const handleAiChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAi(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleEmChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSmtpChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSmtp(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  if (loading) {
    return (
      <PageShell pretitle={t('menu.settings')} title={t('settings.assistant.title')}>
        <CenteredLoader />
      </PageShell>
    );
  }

  return (
    <PageShell
      pretitle={t('menu.settings')}
      title={t('settings.assistant.title')}
      actions={
        <AppButton variant="primary" onClick={handleSave} loading={saving} loadingText={t('common.saving')}>
          {t('common.save')}
        </AppButton>
      }
    >
      {error && <div className="alert alert-danger app-inline-alert" role="alert">{error}</div>}
      {success && <div className="alert alert-success app-inline-alert" role="alert">{success}</div>}

      <Row className="row-cards">
        <Col lg={12}>

          {/* ── AI Provider ───────────────────────────────────────────── */}
          <PageSection
            title={t('settings.assistant.aiSection.title')}
            description={t('settings.assistant.aiSection.subtitle')}
            className="mb-3"
          >
                  <Form.Check
                    type="switch"
                    id="use_local_ai"
                    name="use_local_ai"
                    label={t('settings.assistant.aiSection.useLocalAI')}
                    checked={ai.use_local_ai}
                    onChange={handleAiChange}
                    className="mb-3"
                  />
                  {!ai.use_local_ai && (
                    <>
                      <Form.Group className="mb-3">
                        <Form.Label>{t('settings.assistant.aiSection.openaiApiKey')}</Form.Label>
                        <Form.Control
                          type="password"
                          name="openai_api_key"
                          value={ai.openai_api_key}
                          onChange={handleAiChange}
                          placeholder={ai.openai_api_key_set
                            ? t('settings.assistant.aiSection.apiKeySetPlaceholder')
                            : 'sk-…'}
                          autoComplete="new-password"
                        />
                        {ai.openai_api_key_set && !ai.openai_api_key ? (
                          <div className="d-flex align-items-center justify-content-between mt-1">
                            <Form.Text className="text-success mb-0">
                              ✓ {t('settings.assistant.aiSection.apiKeyIsSet')}
                            </Form.Text>
                            <Button size="sm" variant="outline-danger" onClick={handleUnsetApiKey} disabled={saving}>
                              {t('settings.assistant.aiSection.unsetKey')}
                            </Button>
                          </div>
                        ) : (
                          <Form.Text className="text-muted">{t('settings.assistant.aiSection.apiKeyHelp')}</Form.Text>
                        )}
                      </Form.Group>
                      <Form.Group className="mb-0">
                        <Form.Label>{t('settings.assistant.aiSection.openaiModel')}</Form.Label>
                        <Form.Control
                          type="text"
                          name="openai_model"
                          value={ai.openai_model}
                          onChange={handleAiChange}
                          placeholder="gpt-4o-mini"
                        />
                        <Form.Text className="text-muted">{t('settings.assistant.aiSection.modelHelp')}</Form.Text>
                      </Form.Group>
                    </>
                  )}
          </PageSection>

          {/* ── Email Integration ──────────────────────────────────── */}
          <PageSection
            title={t('settings.assistant.emailSection.title')}
            description={t('settings.assistant.emailSection.subtitle')}
            className="mb-3"
            actions={<Badge bg={em.email_enabled ? 'success' : 'secondary'}>{em.email_enabled ? t('common.enabled') : t('common.disabled')}</Badge>}
          >
                  <Form.Check
                    type="switch"
                    id="email_enabled"
                    name="email_enabled"
                    label={t('settings.assistant.emailSection.enable')}
                    checked={em.email_enabled}
                    onChange={handleEmChange}
                    className="mb-3"
                  />

                  <Row>
                    <Col md={8}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t('settings.assistant.emailSection.host')}</Form.Label>
                        <Form.Control
                          type="text"
                          name="email_host"
                          value={em.email_host}
                          onChange={handleEmChange}
                          placeholder="imap.gmail.com"
                          disabled={!em.email_enabled}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t('settings.assistant.emailSection.port')}</Form.Label>
                        <NumberInput
                          value={em.email_port}
                          onChange={(value) => setEm(prev => ({ ...prev, email_port: value }))}
                          min={1}
                          max={65535}
                          step="any"
                          disabled={!em.email_enabled}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={2} className="d-flex align-items-center pt-1">
                      <Form.Check
                        type="switch"
                        id="email_use_ssl"
                        name="email_use_ssl"
                        label="SSL"
                        checked={em.email_use_ssl}
                        onChange={handleEmChange}
                        disabled={!em.email_enabled}
                        className="mt-3"
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t('settings.assistant.emailSection.username')}</Form.Label>
                        <Form.Control
                          type="email"
                          name="email_username"
                          value={em.email_username}
                          onChange={handleEmChange}
                          placeholder="assistant@yourdomain.com"
                          disabled={!em.email_enabled}
                          autoComplete="off"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t('settings.assistant.emailSection.password')}</Form.Label>
                        <Form.Control
                          type="password"
                          name="email_password"
                          value={em.email_password}
                          onChange={handleEmChange}
                          placeholder={em.email_password_set
                            ? t('settings.assistant.emailSection.passwordSetPlaceholder')
                            : '•••••••'}
                          disabled={!em.email_enabled}
                          autoComplete="new-password"
                        />
                        {em.email_password_set && !em.email_password ? (
                          <div className="d-flex align-items-center justify-content-between mt-1">
                            <Form.Text className="text-success mb-0">
                              ✓ {t('settings.assistant.emailSection.passwordIsSet')}
                            </Form.Text>
                            <Button size="sm" variant="outline-danger" onClick={handleUnsetEmailPassword} disabled={saving || !em.email_enabled}>
                              {t('settings.assistant.emailSection.unsetPassword')}
                            </Button>
                          </div>
                        ) : null}
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t('settings.assistant.emailSection.folder')}</Form.Label>
                        <Form.Control
                          type="text"
                          name="email_folder"
                          value={em.email_folder}
                          onChange={handleEmChange}
                          placeholder="INBOX"
                          disabled={!em.email_enabled}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t('settings.assistant.emailSection.checkInterval')}</Form.Label>
                        <NumberInput
                          value={em.email_check_interval}
                          onChange={(value) => setEm(prev => ({ ...prev, email_check_interval: value }))}
                          min={60}
                          step={60}
                          disabled={!em.email_enabled}
                        />
                        <Form.Text className="text-muted">{t('settings.assistant.emailSection.checkIntervalHelp')}</Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={4} className="d-flex align-items-center pt-1">
                      <Form.Check
                        type="switch"
                        id="email_auto_reply"
                        name="email_auto_reply"
                        label={t('settings.assistant.emailSection.autoReply')}
                        checked={em.email_auto_reply}
                        onChange={handleEmChange}
                        disabled={!em.email_enabled}
                        className="mt-3"
                      />
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>{t('settings.assistant.emailSection.allowedSenders')}</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={5}
                      name="email_allowed_senders"
                      value={em.email_allowed_senders}
                      onChange={handleEmChange}
                      placeholder={'user@example.com\n*@yourcompany.com'}
                      disabled={!em.email_enabled}
                      style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                    />
                    <Form.Text className="text-muted">{t('settings.assistant.emailSection.allowedSendersHelp')}</Form.Text>
                  </Form.Group>

                  {testResult && (
            <div className={`alert ${testResult.success ? 'alert-success' : 'alert-danger'} mb-0 mt-3`}>
              {testResult.message}
            </div>
          )}
          <div className="mt-3">
            <Button
              variant="outline-secondary"
              onClick={handleTestConnection}
              disabled={testing || !em.email_enabled || !em.email_host}
                  >
              {testing
                ? <><span className="spinner-border spinner-border-sm me-2" />{t('settings.assistant.emailSection.testing')}</>
                : <><i className="ti ti-plug me-1" />{t('settings.assistant.emailSection.testConnection')}</>}
            </Button>
          </div>
          </PageSection>

          {/* ── SMTP (outbound replies) ─────────────────────────────── */}
          <PageSection
            title={t('settings.assistant.smtpSection.title')}
            description={t('settings.assistant.smtpSection.subtitle')}
            className="mb-3"
          >
                  <Row className="g-3 mb-3">
                    <Col md={8}>
                      <Form.Group>
                        <Form.Label>{t('settings.assistant.smtpSection.host')}</Form.Label>
                        <Form.Control
                          type="text"
                          name="smtp_host"
                          value={smtp.smtp_host}
                          onChange={handleSmtpChange}
                          placeholder="smtp.gmail.com"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>{t('settings.assistant.smtpSection.port')}</Form.Label>
                        <NumberInput
                          value={smtp.smtp_port}
                          onChange={(value) => setSmtp(prev => ({ ...prev, smtp_port: value }))}
                          min={1}
                          max={65535}
                          step="any"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="g-3 mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>{t('settings.assistant.smtpSection.username')}</Form.Label>
                        <Form.Control
                          type="text"
                          name="smtp_username"
                          value={smtp.smtp_username}
                          onChange={handleSmtpChange}
                          placeholder="assistant@yourcompany.com"
                          autoComplete="off"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>{t('settings.assistant.smtpSection.password')}</Form.Label>
                        <Form.Control
                          type="password"
                          name="smtp_password"
                          value={smtp.smtp_password}
                          onChange={handleSmtpChange}
                          placeholder={smtp.smtp_password_set
                            ? t('settings.assistant.smtpSection.passwordSetPlaceholder')
                            : ''}
                          autoComplete="new-password"
                        />
                        {smtp.smtp_password_set && !smtp.smtp_password && (
                          <div className="d-flex align-items-center gap-2 mt-1">
                            <Badge bg="success" className="text-uppercase" style={{ fontSize: '0.7rem' }}>
                              ✓ {t('settings.assistant.smtpSection.passwordIsSet')}
                            </Badge>
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 text-danger"
                              onClick={handleUnsetSmtpPassword}
                              disabled={saving}
                            >
                              {t('settings.assistant.smtpSection.unsetPassword')}
                            </Button>
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="g-3 mb-3">
                    <Col md={8}>
                      <Form.Group>
                        <Form.Label>{t('settings.assistant.smtpSection.fromEmail')}</Form.Label>
                        <Form.Control
                          type="email"
                          name="smtp_from_email"
                          value={smtp.smtp_from_email}
                          onChange={handleSmtpChange}
                          placeholder="assistant@yourcompany.com"
                        />
                        <Form.Text className="text-muted">{t('settings.assistant.smtpSection.fromEmailHelp')}</Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={4} className="d-flex flex-column gap-2 pt-1">
                      <Form.Check
                        type="switch"
                        id="smtp_use_tls"
                        name="smtp_use_tls"
                        label={t('settings.assistant.smtpSection.useTls')}
                        checked={smtp.smtp_use_tls}
                        onChange={handleSmtpChange}
                        className="mt-3"
                      />
                      <Form.Check
                        type="switch"
                        id="smtp_use_ssl"
                        name="smtp_use_ssl"
                        label={t('settings.assistant.smtpSection.useSsl')}
                        checked={smtp.smtp_use_ssl}
                        onChange={handleSmtpChange}
                      />
                    </Col>
                  </Row>
                  <Form.Text className="text-muted d-block">
                    {t('settings.assistant.smtpSection.fallbackHelp')}
            </Form.Text>
          </PageSection>

        </Col>
      </Row>
    </PageShell>
  );
};

export default AssistantSettings;
